/**
 * The order an edit link opened. Each tab keeps its own in sessionStorage,
 * which no other tab can overwrite, beside the edit token it was opened
 * with; the same id also goes to localStorage, the shared key. A tab with no
 * order of its own sends the shared order's headers, unless it is signed in
 * with a JWT, which sends no edit-link headers at all. A tab opened before tabs
 * kept their own order, which holds its own proof that a link was validated
 * in it, takes the shared order as its own once (`adoptSharedOrder`); an
 * editor URL opened in a fresh tab needs its link opened again. The order and
 * the proof end together (`clearOrderSession`). Every read and write of the
 * order id goes through this module.
 */
export const ORDER_ID_STORAGE_KEY = 'messengerOrderIdToken'

/** Records the order this tab's edit link opened. */
export const storeOrderSession = (orderId: string): void => {
  sessionStorage.setItem(ORDER_ID_STORAGE_KEY, orderId)
  localStorage.setItem(ORDER_ID_STORAGE_KEY, orderId)
}

/** The flags a link's sign-in step leaves as proof that it validated a link. */
const LINK_VALIDATION_KEYS = [
  'tokenValidated',
  'tokenValidatedAt',
  'qcShadowAccess',
] as const

/**
 * Forgets the edit-link order, in this tab and in the shared key, and this
 * tab's proof that a link was validated in it.
 */
export const clearOrderSession = (): void => {
  sessionStorage.removeItem(ORDER_ID_STORAGE_KEY)
  localStorage.removeItem(ORDER_ID_STORAGE_KEY)
  for (const key of LINK_VALIDATION_KEYS) sessionStorage.removeItem(key)
}

/**
 * Whether a link's sign-in step validated an edit link in this tab: the
 * token check's flag, or the QC shadow link's. Both end with the order, so a
 * tab that later served the dashboard holds neither.
 */
export const tabValidatedALink = (): boolean =>
  sessionStorage.getItem('tokenValidated') === 'true' ||
  sessionStorage.getItem('qcShadowAccess') === 'true'

/**
 * Pins a tab opened before tabs kept their own order: one with no order of
 * its own that validated a link takes the shared order as its own, once, so
 * a link opened later in another tab never changes what it sends.
 */
export const adoptSharedOrder = (): void => {
  const shared = localStorage.getItem(ORDER_ID_STORAGE_KEY)
  if (tabOrderId() === null && shared !== null && tabValidatedALink())
    sessionStorage.setItem(ORDER_ID_STORAGE_KEY, shared)
}

/** The order this tab's own edit link opened, if any. */
export const tabOrderId = (): string | null =>
  sessionStorage.getItem(ORDER_ID_STORAGE_KEY)

/**
 * The order this tab acts for as an edit link: its own, or for a tab with
 * none, the order another tab's link last opened.
 */
export const orderSessionId = (): string | null =>
  tabOrderId() ?? localStorage.getItem(ORDER_ID_STORAGE_KEY)
